import apiClient from './apiClient';
import utilityBillCacheService from './utilityBillCacheService';
import anlageVCacheService from './anlageVCacheService';
import statistikCacheService from './statistikCacheService';

/**
 * Cache für Objektkosten-Daten mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu utilityBillCacheService.js:
 * - TTL 5 min (transaktionale Daten)
 * - Explizite Invalidierung nach Mutationen
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `objektkosten:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 Minuten (transaktionale Daten)

const memoryCache = new Map();
const pendingRequests = new Map();

let cacheInvalidationToken = 0;

const isLocalStorageAvailable = () => {
  try {
    const testKey = '__ok_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) return sharedPromise;

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf /api/object-costs-Endpoints.
 * @param {string} url - API-Pfad
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0]
 * @returns {Promise<object|Array>}
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) memoryCache.delete(cacheKey);

  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) removeFromLocalStorage(cacheKey);

  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;

  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    if (currentToken !== cacheInvalidationToken) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || {});
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      if (!staleEntry) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return {};
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = { data, timestamp: Date.now(), etag: responseEtag };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

/**
 * Konvertiert ein Filter-Objekt in Query-Parameter (undefined/leere Werte werden weggelassen).
 * @param {object} filter
 * @returns {object}
 */
const buildParams = (filter = {}) => {
  const params = {};
  const allowed = [
    'year', 'month', 'propertyId', 'propertyGroupId',
    'umlageStatus', 'taxCategory', 'page', 'size', 'sort'
  ];
  for (const key of allowed) {
    const val = filter[key];
    if (val !== undefined && val !== null && val !== '') {
      params[key] = val;
    }
  }
  return Object.keys(params).length > 0 ? params : undefined;
};

const objektkostenService = {
  /**
   * Listet Objektkosten-Positionen (paginiert, gecacht).
   * @param {object} [filter] - Filterparameter (year, month, propertyId, etc.)
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} Spring-Page-Objekt { content, totalElements, ... }
   */
  listObjektkosten: (filter, signal) => {
    const params = buildParams(filter);
    return getCached('/object-costs', params, signal);
  },

  /**
   * Lädt eine einzelne Objektkosten-Position (gecacht).
   * @param {string} id - UUID
   * @param {AbortSignal} [signal]
   * @returns {Promise<object>} ObjectCostDTO
   */
  getObjektkosten: (id, signal) => getCached(`/object-costs/${id}`, undefined, signal),

  /**
   * Erstellt eine neue Objektkosten-Position.
   * @param {object} payload - ObjectCostCreateRequest
   * @returns {Promise<object>} ObjectCostDTO
   */
  createObjektkosten: async (payload) => {
    const response = await apiClient.post('/object-costs', payload);
    objektkostenService.invalidateList();
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Aktualisiert eine Objektkosten-Position (PATCH-Semantik).
   * @param {string} id
   * @param {object} payload - ObjectCostUpdateRequest
   * @returns {Promise<object>} ObjectCostDTO
   */
  updateObjektkosten: async (id, payload) => {
    const response = await apiClient.patch(`/object-costs/${id}`, payload);
    objektkostenService.invalidateList();
    objektkostenService.invalidateDetail(id);
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Löscht eine Objektkosten-Position.
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteObjektkosten: async (id) => {
    await apiClient.delete(`/object-costs/${id}`);
    objektkostenService.invalidateList();
    objektkostenService.invalidateDetail(id);
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
  },

  /**
   * Verknüpft ein PropertyFile-Dokument mit einer Objektkosten-Position.
   * @param {string} id - ObjectCost-UUID
   * @param {string} propertyFileId - PropertyFile-UUID
   * @returns {Promise<object>} Aktualisiertes ObjectCostDTO
   */
  attachDocument: async (id, propertyFileId) => {
    const response = await apiClient.post(`/object-costs/${id}/attach-document`, { propertyFileId });
    objektkostenService.invalidateDetail(id);
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Entfernt den verknüpften Beleg einer Objektkosten-Position.
   * @param {string} id - ObjectCost-UUID
   * @returns {Promise<object>} Aktualisiertes ObjectCostDTO
   */
  detachDocument: async (id) => {
    const response = await apiClient.delete(`/object-costs/${id}/document`);
    objektkostenService.invalidateDetail(id);
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Invalidiert den List-Cache (nach Mutationen).
   */
  invalidateList: () => {
    cacheInvalidationToken++;
    for (const key of [...memoryCache.keys()]) {
      if (key === '/object-costs' || key.startsWith('/object-costs?')) {
        memoryCache.delete(key);
        pendingRequests.delete(key);
        removeFromLocalStorage(key);
      }
    }
    if (storageAvailable) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const storageKey = localStorage.key(i);
          if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
            const cacheKey = storageKey.substring(CACHE_PREFIX.length);
            if (cacheKey === '/object-costs' || cacheKey.startsWith('/object-costs?')) {
              localStorage.removeItem(storageKey);
            }
          }
        }
      } catch {
        // Ignorieren
      }
    }
  },

  /**
   * Invalidiert den Detail-Cache für eine bestimmte Position.
   * @param {string} id
   */
  invalidateDetail: (id) => {
    const detailKey = `/object-costs/${id}`;
    invalidateCacheKey(detailKey);
  },

  /**
   * Invalidiert alle Objektkosten-Caches.
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Analysiert eine Objektkosten-PDF ohne persistente Speicherung.
   * @param {File} file - Die hochzuladende PDF-Datei
   * @param {{ propertyId?: string, propertyGroupId?: string }} scope - Genau eines der beiden Felder muss gesetzt sein
   * @param {AbortSignal} [signal] - AbortController-Signal für Abbruch
   * @returns {Promise<ObjectCostPdfAnalyzeResponse>}
   */
  analyzeObjectCostPdf: async (file, { propertyId, propertyGroupId } = {}, signal) => {
    const formData = new FormData();
    formData.append('file', file);

    const params = {};
    if (propertyId) params.propertyId = propertyId;
    if (propertyGroupId) params.propertyGroupId = propertyGroupId;

    const response = await apiClient.post('/object-costs/analyze-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
      timeout: 90000,
      signal
    });
    return response.data;
  },

  /**
   * Bestätigt und persistiert einen PDF-Import (idempotent per importToken).
   * Nach Erfolg wird der Objektkosten-Cache, der UtilityBill-Cache und (soweit
   * vorhanden) alle caches für abgeleitete Buchungen invalidiert.
   * @param {object} payload - ObjectCostPdfImportConfirmRequest
   * @returns {Promise<ObjectCostPdfImportConfirmResponse>}
   */
  confirmObjectCostPdfImport: async (payload) => {
    const response = await apiClient.post('/object-costs/import-confirm', payload);
    // Objektkosten-List-Cache invalidieren
    objektkostenService.invalidateList();
    // UtilityBill-Cache invalidieren (abgeleitete NK-Positionen können angelegt worden sein)
    utilityBillCacheService.invalidateList();
    // Anlage-V-Cache invalidieren (neue Kosten ändern das Berechnungsergebnis)
    anlageVCacheService.invalidateAll();
    statistikCacheService.invalidateAll();
    return response.data;
  }
};

export default objektkostenService;
